/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { databaseService } from '../database/database.service.js';

class FamachaRepository {
  /**
   * Get all FAMACHA checks for a goat (latest first)
   * @param {number} goatId
   * @param {number|null} limit
   * @returns {Promise<Array<import('../../models/famacha.model.js').FamachaCheck>>}
   */
  async findByGoat(goatId, limit = null) {
    const base = 'SELECT * FROM famacha WHERE goat_id = ? ORDER BY measured_at DESC';
    const sql = limit ? `${base} LIMIT ?` : base;
    const params = limit ? [goatId, limit] : [goatId];

    const res = await databaseService.query(sql, params);
    return (res.values || []).map(row => ({
      id: row.id,
      goatId: row.goat_id,
      score: row.score,
      measuredAt: row.measured_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    }));
  }

  /**
   * Get a single FAMACHA check by ID
   * @param {number} id
   * @returns {Promise<import('../../models/famacha.model.js').FamachaCheck|null>}
   */
  async findById(id) {
    const sql = 'SELECT * FROM famacha WHERE id = ?';
    const res = await databaseService.query(sql, [id]);
    
    if (!res.values || res.values.length === 0) {
      return null;
    }

    const row = res.values[0];
    return {
      id: row.id,
      goatId: row.goat_id,
      score: row.score,
      measuredAt: row.measured_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    };
  }

  /**
   * Create a new FAMACHA check entry
   * @param {{goatId:number, score:number, measuredAt?:string}} payload
   * @returns {Promise<number|null>} last inserted id
   */
  async create({ goatId, score, measuredAt = null }) {
    const sql = measuredAt
      ? 'INSERT INTO famacha (goat_id, score, measured_at) VALUES (?, ?, ?)'
      : 'INSERT INTO famacha (goat_id, score) VALUES (?, ?)';
    const params = measuredAt ? [goatId, score, measuredAt] : [goatId, score];
    const r = await databaseService.run(sql, params);
    return r?.changes?.lastId ?? null;
  }

  /**
   * Update an existing FAMACHA check record
   * @param {number} id
   * @param {{score:number, measuredAt?:string}} payload
   */
  async update(id, { score, measuredAt = null }) {
    const sql = measuredAt
      ? 'UPDATE famacha SET score = ?, measured_at = ?, updated_at = CURRENT_TIMESTAMP WHERE id = ?'
      : 'UPDATE famacha SET score = ?, updated_at = CURRENT_TIMESTAMP WHERE id = ?';
    const params = measuredAt ? [score, measuredAt, id] : [score, id];
    await databaseService.run(sql, params);
  }

  /**
   * Delete a FAMACHA check entry
   * @param {number} id
   */
  async delete(id) {
    await databaseService.run('DELETE FROM famacha WHERE id = ?', [id]);
  }

  /**
   * Delete all FAMACHA checks for a specific goat
   * @param {number} goatId
   */
  async deleteByGoat(goatId) {
    await databaseService.run('DELETE FROM famacha WHERE goat_id = ?', [goatId]);
  }

  /**
   * Get the latest FAMACHA check for a goat
   * @param {number} goatId
   * @returns {Promise<import('../../models/famacha.model.js').FamachaCheck|null>}
   */
  async latest(goatId) {
    const rows = await this.findByGoat(goatId, 1);
    return rows[0] ?? null;
  }

  /**
   * Get all FAMACHA checks (useful for analytics/reports)
   * @returns {Promise<Array<import('../../models/famacha.model.js').FamachaCheck>>}
   */
  async findAll() {
    const sql = 'SELECT * FROM famacha ORDER BY measured_at DESC';
    const res = await databaseService.query(sql);
    return (res.values || []).map(row => ({
      id: row.id,
      goatId: row.goat_id,
      score: row.score,
      measuredAt: row.measured_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    }));
  }
}

export const famachaRepository = new FamachaRepository();
